import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

import { Card, Modal, Button, Row, Col } from "react-bootstrap";

import {
  FiInfo,
  FiX,
  FiBarChart2,
  FiCheckCircle,
  FiClock,
  FiTruck,
  FiMaximize2,
} from "react-icons/fi";

const GraficoOrdiniFornitori = () => {
  const [dataGrafico, setDataGrafico] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showFullscreenModal, setShowFullscreenModal] = useState(false);

  // NUOVA MODALE INFORMATIVA
  const [showInfoModal, setShowInfoModal] = useState(false);

  useEffect(() => {
    fetch("ordini_urgenti.json")
      .then((res) => res.json())
      .then((json) => {
        const ordiniRaw = json?.ORDINI_URGENTI?.flat() || [];

        const raggruppati = ordiniRaw.reduce((acc, curr) => {
          const nome = curr["Ragione sociale"] || "Sconosciuto";

          const parseImporto = (val) => {
            if (!val) return 0;

            return (
              parseFloat(
                String(val).replace(/[^0-9.-]/g, "")
              ) || 0
            );
          };

          const importoEva = parseImporto(curr["Importo Evaso"]);
          const importoDev = parseImporto(curr["Importo Da Evadere"]);
          const importoDevRim = parseImporto(
            curr["Importo Da Evadere rim"]
          );

          if (!acc[nome]) {
            acc[nome] = {
              evaso: 0,
              daEvadereStandard: 0,
              daEvadereRimesso: 0,
            };
          }

          /*
           * IMPORTO EVASO
           *
           * È la quota economica dell'ordine già evasa.
           */
          acc[nome].evaso += importoEva;

          /*
           * IMPORTO DA EVADERE
           *
           * Se "Importo Da Evadere rim" è > 0
           * significa che la quantità ancora da evadere
           * risulta già rimessa.
           *
           * In questo caso l'importo viene classificato
           * come DA EVADERE RIMESSO.
           *
           * Altrimenti rimane DA EVADERE STANDARD.
           */
          if (importoDevRim > 0) {
            acc[nome].daEvadereRimesso += importoDev;
          } else {
            acc[nome].daEvadereStandard += importoDev;
          }

          return acc;
        }, {});

        /*
         * CREAZIONE DATI GRAFICO
         *
         * Il totale della colonna è composto da:
         *
         * EVASO
         * +
         * DA EVADERE STANDARD
         * +
         * DA EVADERE RIMESSO
         */
        const formattedData = Object.keys(raggruppati)
          .map((key) => {
            const evaso = parseFloat(
              raggruppati[key].evaso.toFixed(2)
            );

            const daEvadereStandard = parseFloat(
              raggruppati[key].daEvadereStandard.toFixed(2)
            );

            const daEvadereRimesso = parseFloat(
              raggruppati[key].daEvadereRimesso.toFixed(2)
            );

            return {
              name: key,

              importoEvaso: evaso,

              importoDaEvadereStandard:
                daEvadereStandard,

              importoDaEvadereRimesso:
                daEvadereRimesso,

              totaleColonna: parseFloat(
                (
                  evaso +
                  daEvadereStandard +
                  daEvadereRimesso
                ).toFixed(2)
              ),
            };
          })

          // Ordina dal fornitore con importo maggiore
          // a quello con importo minore
          .sort(
            (a, b) =>
              b.totaleColonna - a.totaleColonna
          )

          // Mostra solo i primi 10
          .slice(0, 10);

        setDataGrafico(formattedData);

        setLoading(false);
      })
      .catch((err) => {
        console.error(
          "Errore fetch grafico:",
          err
        );

        setLoading(false);
      });
  }, []);

  /*
   * Abbrevia i nomi nella visualizzazione compatta.
   */
  const formatXAxis = (tickItem) => {
    return tickItem.length > 12
      ? `${tickItem.substring(0, 12)}...`
      : tickItem;
  };

  /*
   * CONTENUTO DEL GRAFICO
   *
   * Utilizzato sia nella card normale
   * sia nella vista fullscreen.
   */
  const renderChartContent = (
    isFullscreen = false
  ) => (
    <ResponsiveContainer
      width="100%"
      height="100%"
    >
      <BarChart
        data={dataGrafico}
        margin={
          isFullscreen
            ? {
                top: 10,
                right: 30,
                left: 20,
                bottom: 60,
              }
            : {
                top: 0,
                right: 30,
                left: 10,
                bottom: 100,
              }
        }
      >
        <CartesianGrid
          strokeDasharray="3 3"
          vertical={false}
          stroke="#eee"
        />

        <XAxis
          dataKey="name"
          tickFormatter={
            isFullscreen
              ? undefined
              : formatXAxis
          }
          interval={0}
          angle={
            isFullscreen ? -25 : -40
          }
          textAnchor="end"
          style={{
            fontSize: isFullscreen
              ? "12px"
              : "11px",
            fontWeight: "500",
            fill: "#6c757d",
          }}
          dy={10}
        />

        <YAxis
          tickFormatter={(value) =>
            `€ ${value.toLocaleString(
              "it-IT"
            )}`
          }
          style={{
            fontSize: "11px",
            fill: "#6c757d",
          }}
          axisLine={false}
          tickLine={false}
        />

        <RechartsTooltip
          cursor={{
            fill: "#f8f9fa",
          }}
          formatter={(value, name) => {
            let label = "";

            if (
              name === "importoEvaso"
            ) {
              label =
                "Importo Evaso";
            } else if (
              name ===
              "importoDaEvadereStandard"
            ) {
              label =
                "Importo da Evadere";
            } else if (
              name ===
              "importoDaEvadereRimesso"
            ) {
              label =
                "Importo da Evadere (Rimesso)";
            }

            return [
              `€ ${Number(
                value
              ).toLocaleString(
                "it-IT",
                {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }
              )}`,
              label,
            ];
          }}
          contentStyle={{
            borderRadius: "12px",
            border: "none",
            boxShadow:
              "0 8px 24px rgba(0,0,0,0.15)",
            padding: "10px",
          }}
        />

        <Legend
          verticalAlign="top"
          height={36}
          formatter={(value) => {
            if (
              value === "importoEvaso"
            ) {
              return "Importo Evaso";
            }

            if (
              value ===
              "importoDaEvadereStandard"
            ) {
              return "Importo da Evadere";
            }

            if (
              value ===
              "importoDaEvadereRimesso"
            ) {
              return "Importo da Evadere (Rimesso)";
            }

            return value;
          }}
        />

        {/* EVASO */}
        <Bar
          dataKey="importoEvaso"
          stackId="a"
          fill="#dc2626"
          radius={0}
          barSize={
            isFullscreen ? 50 : 35
          }
        />

        {/* DA EVADERE STANDARD */}
        <Bar
          dataKey="importoDaEvadereStandard"
          stackId="a"
          fill="#6c757d"
          radius={0}
          barSize={
            isFullscreen ? 50 : 35
          }
        />

        {/* DA EVADERE RIMESSO */}
        <Bar
          dataKey="importoDaEvadereRimesso"
          stackId="a"
          fill="#000000"
          radius={[6, 6, 0, 0]}
          barSize={
            isFullscreen ? 50 : 35
          }
        />
      </BarChart>
    </ResponsiveContainer>
  );

  if (loading) {
    return (
      <div className="text-center p-5 text-muted">
        Caricamento grafico in corso...
      </div>
    );
  }

  return (
    <>
      <Card
        className="border-0 overflow-hidden"
        style={{
          borderRadius: "10px",
          height: "420px",
        }}
      >
        {/* ==========================
            HEADER
        ========================== */}

        <div
          className="p-3 border-bottom d-flex justify-content-between align-items-center bg-white"
          style={{ zIndex: 110 }}
        >
          <div className="d-flex align-items-center">
            {/* TITOLO */}
            <span
              onClick={() =>
                setShowFullscreenModal(true)
              }
              style={{
                fontWeight: "800",
                fontSize: "0.75rem",
                color: "#1a1a1a",
                letterSpacing: "1px",
                cursor: "pointer",
              }}
            >
              TOP 10 FORNITORI DEL MESE
            </span>

            {/* INFO */}
            <button
              type="button"
              onClick={() =>
                setShowInfoModal(true)
              }
              className="grafico-info-btn ms-2"
              aria-label="Informazioni sul grafico"
            >
              <FiInfo size={14} />
            </button>
          </div>
        </div>

        {/* ==========================
            GRAFICO
        ========================== */}

        <div
          className="p-2 flex-grow-1"
          style={{
            height: "420px",
          }}
        >
          {renderChartContent(false)}
        </div>
      </Card>

      {/* ==========================================================
          MODALE INFORMAZIONI
      ========================================================== */}

      <Modal
        show={showInfoModal}
        onHide={() =>
          setShowInfoModal(false)
        }
        centered
        size="lg"
        scrollable
        className="p-5"
        style={{
          zIndex: 21000,
        }}
        backdropClassName="grafico-info-backdrop"
      >
        {/* HEADER INFO */}
        <Modal.Header
          closeButton
          className="border-bottom bg-white px-4 py-3"
        >
          <div className="d-flex align-items-center gap-3">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary flex-shrink-0"
              style={{
                width: "40px",
                height: "40px",
              }}
            >
              <FiBarChart2 size={20} />
            </div>

            <div>
              <Modal.Title
                className="fw-bold"
                style={{
                  fontSize: "1.05rem",
                }}
              >
                Come leggere il grafico
                fornitori
              </Modal.Title>

              <div
                className="text-muted mt-1"
                style={{
                  fontSize: "0.78rem",
                }}
              >
                Guida alla composizione degli
                importi e alla posizione dei
                fornitori nella classifica.
              </div>
            </div>
          </div>
        </Modal.Header>

        {/* BODY INFO */}
        <Modal.Body className="bg-light p-4">
          {/* INTRODUZIONE */}
          <div className="bg-primary-subtle border border-primary-subtle rounded-4 p-3 mb-3">
            <div className="fw-bold text-primary mb-1">
              A cosa serve
            </div>

            <div
              className="text-dark"
              style={{
                fontSize: "0.84rem",
                lineHeight: 1.55,
              }}
            >
              Il grafico permette di
              confrontare rapidamente i
              principali fornitori in base al
              valore economico degli ordini
              presenti nel monitoraggio. Ogni
              colonna rappresenta un fornitore
              ed è suddivisa nelle diverse
              componenti dello stato
              dell'ordine.
            </div>
          </div>

          {/* ==========================
              SEZIONE 1
          ========================== */}

          <Row className="g-3 mb-3">
            <Col md={6}>
              <div className="grafico-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="grafico-info-step">
                    1
                  </span>

                  <div>
                    <div className="fw-bold text-dark">
                      Come viene creata la
                      classifica
                    </div>

                    <div
                      className="text-muted"
                      style={{
                        fontSize:
                          "0.75rem",
                      }}
                    >
                      Ordinamento dei Top 10
                      fornitori
                    </div>
                  </div>
                </div>

                <ul className="grafico-info-list">
                  <li>
                    Gli ordini vengono
                    raggruppati per{" "}
                    <strong>
                      ragione sociale del
                      fornitore
                    </strong>
                    .
                  </li>

                  <li>
                    Per ogni fornitore vengono
                    sommati tutti gli importi
                    presenti negli ordini.
                  </li>

                  <li>
                    Il valore complessivo è
                    dato dalla somma di{" "}
                    <strong>
                      Evaso + Da Evadere + Da
                      Evadere Rimesso
                    </strong>
                    .
                  </li>

                  <li>
                    I fornitori vengono
                    ordinati dal valore
                    economico complessivo più
                    alto al più basso.
                  </li>

                  <li>
                    Il grafico mostra i{" "}
                    <strong>
                      primi 10 fornitori
                    </strong>{" "}
                    risultanti da questa
                    classifica.
                  </li>
                </ul>
              </div>
            </Col>

            {/* ==========================
                SEZIONE 2
            ========================== */}

            <Col md={6}>
              <div className="grafico-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="grafico-info-step">
                    2
                  </span>

                  <div>
                    <div className="fw-bold text-dark">
                      Struttura della colonna
                    </div>

                    <div
                      className="text-muted"
                      style={{
                        fontSize:
                          "0.75rem",
                      }}
                    >
                      Un'unica colonna,
                      suddivisa per stato
                    </div>
                  </div>
                </div>

                <ul className="grafico-info-list">
                  <li>
                    L'altezza totale della
                    colonna rappresenta il{" "}
                    <strong>
                      valore economico
                      complessivo
                    </strong>{" "}
                    associato al fornitore.
                  </li>

                  <li>
                    La colonna è costruita in
                    modalità{" "}
                    <strong>stacked</strong>:
                    le diverse componenti sono
                    sovrapposte.
                  </li>

                  <li>
                    Una colonna più alta
                    identifica quindi un
                    fornitore con un valore
                    economico maggiore nel
                    monitoraggio.
                  </li>
                </ul>
              </div>
            </Col>
          </Row>

          {/* ==========================
              LE TRE COMPONENTI
          ========================== */}

          <div className="grafico-info-card mb-3">
            <div className="d-flex align-items-center gap-2 mb-3">
              <span className="grafico-info-step">
                3
              </span>

              <div>
                <div className="fw-bold text-dark">
                  Significato degli importi
                </div>

                <div
                  className="text-muted"
                  style={{
                    fontSize: "0.75rem",
                  }}
                >
                  Come interpretare le tre
                  sezioni della colonna
                </div>
              </div>
            </div>

            <Row className="g-3">
              {/* EVASO */}
              <Col md={4}>
                <div className="border rounded-4 p-3 h-100 bg-white">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div
                      className="rounded-circle"
                      style={{
                        width: "12px",
                        height: "12px",
                        background:
                          "#dc2626",
                      }}
                    />

                    <FiCheckCircle
                      size={16}
                      className="text-danger"
                    />

                    <strong>
                      Importo Evaso
                    </strong>
                  </div>

                  <div
                    className="text-muted"
                    style={{
                      fontSize: "0.8rem",
                      lineHeight: 1.5,
                    }}
                  >
                    Rappresenta la quota
                    economica degli ordini che
                    risulta già evasa. È la
                    parte dell'ordine per cui
                    il processo di evasione è
                    già stato registrato.
                  </div>
                </div>
              </Col>

              {/* DA EVADERE */}
              <Col md={4}>
                <div className="border rounded-4 p-3 h-100 bg-white">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div
                      className="rounded-circle"
                      style={{
                        width: "12px",
                        height: "12px",
                        background:
                          "#6c757d",
                      }}
                    />

                    <FiClock
                      size={16}
                      className="text-secondary"
                    />

                    <strong>
                      Da Evadere
                    </strong>
                  </div>

                  <div
                    className="text-muted"
                    style={{
                      fontSize: "0.8rem",
                      lineHeight: 1.5,
                    }}
                  >
                    Rappresenta il valore
                    economico ancora da
                    evadere che non risulta
                    associato a un rimesso.
                    Indica quindi la quota
                    ancora aperta nel normale
                    flusso dell'ordine.
                  </div>
                </div>
              </Col>

              {/* RIMESSO */}
              <Col md={4}>
                <div className="border rounded-4 p-3 h-100 bg-white">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div
                      className="rounded-circle"
                      style={{
                        width: "12px",
                        height: "12px",
                        background:
                          "#000000",
                      }}
                    />

                    <FiTruck
                      size={16}
                      className="text-dark"
                    />

                    <strong>
                      Da Evadere Rimesso
                    </strong>
                  </div>

                  <div
                    className="text-muted"
                    style={{
                      fontSize: "0.8rem",
                      lineHeight: 1.5,
                    }}
                  >
                    È la quota ancora da
                    evadere che risulta però
                    già collegata a un{" "}
                    <strong>rimesso</strong>.
                    L'importo non è quindi
                    ancora evaso, ma il
                    processo logistico è già
                    avanzato rispetto a un
                    normale ordine aperto.
                  </div>
                </div>
              </Col>
            </Row>
          </div>

          {/* ==========================
              LOGICA RIMESSO
          ========================== */}

          <Row className="g-3 mb-3">
            <Col md={6}>
              <div className="grafico-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="grafico-info-step">
                    4
                  </span>

                  <div>
                    <div className="fw-bold text-dark">
                      Come viene identificato
                      il Rimesso
                    </div>

                    <div
                      className="text-muted"
                      style={{
                        fontSize:
                          "0.75rem",
                      }}
                    >
                      Classificazione
                      automatica dell'importo
                    </div>
                  </div>
                </div>

                <div
                  className="text-dark"
                  style={{
                    fontSize: "0.82rem",
                    lineHeight: 1.55,
                  }}
                >
                  Il sistema controlla il
                  valore{" "}
                  <strong>
                    Importo Da Evadere rim
                  </strong>
                  .
                </div>

                <div className="mt-3 p-3 rounded-3 bg-light border">
                  <div
                    className="mb-2"
                    style={{
                      fontSize: "0.8rem",
                    }}
                  >
                    <strong>
                      Se il valore è maggiore
                      di 0:
                    </strong>
                  </div>

                  <div
                    className="text-muted"
                    style={{
                      fontSize: "0.78rem",
                    }}
                  >
                    l'Importo Da Evadere viene
                    classificato come{" "}
                    <strong>
                      Da Evadere Rimesso
                    </strong>
                    .
                  </div>
                </div>

                <div className="mt-2 p-3 rounded-3 bg-light border">
                  <div
                    className="mb-2"
                    style={{
                      fontSize: "0.8rem",
                    }}
                  >
                    <strong>
                      Se il valore è uguale a
                      0:
                    </strong>
                  </div>

                  <div
                    className="text-muted"
                    style={{
                      fontSize: "0.78rem",
                    }}
                  >
                    l'importo rimane nella
                    categoria{" "}
                    <strong>
                      Da Evadere
                    </strong>{" "}
                    standard.
                  </div>
                </div>
              </div>
            </Col>

            {/* ==========================
                COME LEGGERE
            ========================== */}

            <Col md={6}>
              <div className="grafico-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="grafico-info-step">
                    5
                  </span>

                  <div>
                    <div className="fw-bold text-dark">
                      Come leggere il grafico
                    </div>

                    <div
                      className="text-muted"
                      style={{
                        fontSize:
                          "0.75rem",
                      }}
                    >
                      Analisi rapida della
                      situazione
                    </div>
                  </div>
                </div>

                <ul className="grafico-info-list">
                  <li>
                    Una forte presenza di{" "}
                    <strong>
                      Importo Evaso
                    </strong>{" "}
                    indica che una parte
                    importante del valore è
                    già stata gestita.
                  </li>

                  <li>
                    Una forte presenza di{" "}
                    <strong>
                      Da Evadere
                    </strong>{" "}
                    indica invece un valore
                    ancora aperto.
                  </li>

                  <li>
                    Una forte presenza di{" "}
                    <strong>
                      Da Evadere Rimesso
                    </strong>{" "}
                    identifica valore ancora
                    aperto ma già avanzato nel
                    processo logistico.
                  </li>

                  <li>
                    Passando con il mouse
                    sopra una colonna puoi
                    visualizzare l'importo
                    preciso di ogni
                    componente.
                  </li>
                </ul>
              </div>
            </Col>
          </Row>

          {/* ==========================
              ESEMPIO
          ========================== */}

          <div className="grafico-info-card mb-3">
            <div className="d-flex align-items-center gap-2 mb-3">
              <span className="grafico-info-step">
                6
              </span>

              <div>
                <div className="fw-bold text-dark">
                  Esempio pratico
                </div>

                <div
                  className="text-muted"
                  style={{
                    fontSize: "0.75rem",
                  }}
                >
                  Come viene costruita una
                  singola colonna
                </div>
              </div>
            </div>

            <div
              className="p-3 rounded-4 border bg-light"
              style={{
                fontSize: "0.82rem",
              }}
            >
              <div className="fw-bold mb-2">
                Fornitore Alfa
              </div>

              <Row className="g-2">
                <Col md={4}>
                  <div className="bg-white border rounded-3 p-2 h-100">
                    <div className="text-muted small">
                      Evaso
                    </div>

                    <div className="fw-bold text-danger">
                      € 20.000
                    </div>
                  </div>
                </Col>

                <Col md={4}>
                  <div className="bg-white border rounded-3 p-2 h-100">
                    <div className="text-muted small">
                      Da Evadere
                    </div>

                    <div className="fw-bold text-secondary">
                      € 10.000
                    </div>
                  </div>
                </Col>

                <Col md={4}>
                  <div className="bg-white border rounded-3 p-2 h-100">
                    <div className="text-muted small">
                      Da Evadere Rimesso
                    </div>

                    <div className="fw-bold text-dark">
                      € 5.000
                    </div>
                  </div>
                </Col>
              </Row>

              <div className="mt-3 pt-3 border-top">
                Valore totale utilizzato per
                la classifica:{" "}
                <strong>
                  € 35.000
                </strong>
              </div>
            </div>
          </div>

          {/* ==========================
              FULLSCREEN
          ========================== */}

          <div className="grafico-info-card">
            <div className="d-flex align-items-center gap-2 mb-3">
              <span className="grafico-info-step">
                7
              </span>

              <div>
                <div className="fw-bold text-dark">
                  Vista dettagliata
                </div>

                <div
                  className="text-muted"
                  style={{
                    fontSize: "0.75rem",
                  }}
                >
                  Grafico a schermo intero
                </div>
              </div>
            </div>

            <div className="d-flex gap-3 align-items-start">
              <div
                className="d-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary flex-shrink-0"
                style={{
                  width: "36px",
                  height: "36px",
                }}
              >
                <FiMaximize2 size={16} />
              </div>

              <div
                style={{
                  fontSize: "0.82rem",
                  lineHeight: 1.55,
                }}
              >
                Cliccando sul titolo{" "}
                <strong>
                  TOP 10 FORNITORI DEL MESE
                </strong>{" "}
                puoi aprire il grafico a
                schermo intero. Nella vista
                dettagliata i nomi dei
                fornitori sono visualizzati
                con maggiore spazio e le
                colonne risultano più facili
                da confrontare.
              </div>
            </div>
          </div>
        </Modal.Body>

        {/* FOOTER INFO */}
        <Modal.Footer className="bg-white border-top px-4 py-3 d-flex justify-content-between">
          <div
            className="text-muted"
            style={{
              fontSize: "0.75rem",
            }}
          >
            Suggerimento: confronta sia
            l'altezza totale della colonna sia
            la composizione interna degli
            importi.
          </div>

          <div className="d-flex gap-2">
            <Button
              variant="light"
              onClick={() =>
                setShowInfoModal(false)
              }
            >
              Chiudi
            </Button>

            <Button
              variant="primary"
              onClick={() => {
                setShowInfoModal(false);
                setShowFullscreenModal(true);
              }}
            >
              Apri vista estesa
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* ==========================================================
          MODALE FULLSCREEN
      ========================================================== */}

      <Modal
        show={showFullscreenModal}
        onHide={() =>
          setShowFullscreenModal(false)
        }
        fullscreen
        animation
      >
        <Modal.Body
          className="p-0 d-flex flex-column"
          style={{
            background: "#f8f9fa",
          }}
        >
          {/* HEADER FULLSCREEN */}
          <div
            className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center shadow-sm"
            style={{
              zIndex: 110,
            }}
          >
            <div className="d-flex align-items-center">
              <span
                style={{
                  fontWeight: "800",
                  fontSize: "0.9rem",
                  color: "#1a1a1a",
                  letterSpacing: "1px",
                  marginRight: "8px",
                }}
              >
                TOP 10 FORNITORI - VISTA
                DETTAGLIATA FLUSSI
              </span>

              {/* INFO ANCHE NELLA FULLSCREEN */}
              <button
                type="button"
                onClick={() =>
                  setShowInfoModal(true)
                }
                className="grafico-info-btn ms-2"
                aria-label="Informazioni sul grafico"
              >
                <FiInfo size={16} />
              </button>
            </div>

            <Button
              variant="outline-dark"
              className="d-flex align-items-center gap-1 border-0"
              onClick={() =>
                setShowFullscreenModal(false)
              }
              style={{
                borderRadius: "10px",
                height: "38px",
              }}
            >
              <FiX size={16} />
              Chiudi
            </Button>
          </div>

          {/* GRAFICO FULLSCREEN */}
          <div
            className="flex-grow-1 p-4 d-flex align-items-center justify-content-center"
            style={{
              overflowY: "auto",
            }}
          >
            <div
              className="bg-white p-4 shadow-sm rounded-4 border w-100"
              style={{
                maxWidth: "1200px",
                height: "550px",
              }}
            >
              {renderChartContent(true)}
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* ==========================================================
          STILI
      ========================================================== */}

      <style>{`
        .grafico-info-btn {
          width: 27px;
          height: 27px;
          border: 1px solid #dfe3e8;
          border-radius: 50%;
          background: #fff;
          color: #0d6efd;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          transition: all 0.15s ease;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          cursor: pointer;
        }

        .grafico-info-btn:hover {
          background: #e7f1ff;
          border-color: #b6d4fe;
          transform: translateY(-1px);
        }

        .grafico-info-backdrop {
          z-index: 20990 !important;
        }

        .grafico-info-card {
          border: 1px solid #e9ecef;
          border-radius: 14px;
          background: #fff;
          padding: 16px;
          height: 100%;
        }

        .grafico-info-step {
          width: 28px;
          height: 28px;
          min-width: 28px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.72rem;
          font-weight: 800;
          background: #e7f1ff;
          color: #0d6efd;
        }

        .grafico-info-list {
          margin: 0;
          padding-left: 18px;
          color: #495057;
          font-size: 0.82rem;
        }

        .grafico-info-list li + li {
          margin-top: 7px;
        }
      `}</style>
    </>
  );
};

export default GraficoOrdiniFornitori;